import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { OnwardLink, SecondaryButton } from '@/styles/blocks';
import { patchEvent } from '@onward-delivery/core';
import { Grid } from '@material-ui/core';
import { ResponsiveSidebarDialog, StickyModalActions, ModalContent, PrimaryButton } from '@/styles/blocks';
import { genAttributes } from '@onward-delivery/core';
import { Body1, DateTimePicker } from '../Crossdocking/blocks';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { Alert } from '@material-ui/lab';
import {
    findMatchingWarehouseEvent,
    getWarehouseEventUpsertColumns,
    toWarehouseEventInput,
} from '@/utilities/warehouseEvents';

const ReceiveModal = ({
    events,
    action,
    timestampLabel,
    timestampAttr,
    description1,
    description2,
    params,
    order,
    callbacks,
}) => {
    const [updates, setUpdates] = useState({});

    useEffect(() => {
        setUpdates({});
    }, [action, timestampAttr]);

    const prev = useMemo(() => {
        return findMatchingWarehouseEvent(events, { action });
    }, [events, action]);

    const updated = useMemo(() => {
        const changes = {
            ...prev,
            [timestampAttr]: prev?.[timestampAttr] || new Date().toISOString(),
            ...(params || {}),
            ...updates,
        };

        return {
            ...changes,
            status: params?.status ?? (changes.received_date ? 'RECEIVED' : 'NOT_DELIVERED'),
        };
    }, [params, prev, timestampAttr, updates]);

    const tz = useMemo(() => {
        const { zip } = genAttributes(order);
        return zipcode_to_timezone.lookup(order[zip]) || 'America/New_York';
    }, [order]);

    const submit = () => {
        const previous = findMatchingWarehouseEvent(order?.wh_events, { eventId: prev.event_id, action });
        const toInsert = patchEvent({
            curr: events,
            prev: order?.wh_events,
            action,
            patch: updated,
        }).map(toWarehouseEventInput);
        const target = findMatchingWarehouseEvent(toInsert, { action });
        const cols = getWarehouseEventUpsertColumns(prev, previous, target, { timestampAttr, params });
        callbacks.onSubmit({
            events: toInsert,
            cols,
        });
    };

    return (
        <ResponsiveSidebarDialog open={events?.length > 0 || !!action} onClose={callbacks.onClose}>
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 12px;
                    `}
                >
                    <Body1>{description1}</Body1>
                </Grid>
                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 12px;
                    `}
                >
                    <Body1>{description2}</Body1>
                </Grid>
                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 12px;
                    `}
                >
                    <DateTimePicker
                        dateLabel={timestampLabel}
                        tz={tz}
                        timestamp={updated[timestampAttr]}
                        onChange={(date) =>
                            setUpdates((prev) => ({ ...prev, [timestampAttr]: date ? date.toISOString() : null }))
                        }
                    />
                </Grid>
                <Alert severity="warning">
                    <Body1>
                        {'Please report any overages, shortages, or damages to '}
                        <OnwardLink href={'mailto:osd@onwarddelivery.com'}>osd@onwarddelivery.com</OnwardLink>
                    </Body1>
                </Alert>
            </ModalContent>
            <StickyModalActions>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <SecondaryButton onClick={callbacks.onClose}>Cancel</SecondaryButton>
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <PrimaryButton onClick={submit}>Confirm</PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default ReceiveModal;
